import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, MapPin, Briefcase, Filter, X, Building2, 
  ArrowUpRight, Clock, Megaphone, Sparkles, Play, FileText 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { jobsAPI } from '../lib/api';
import { formatSalary, formatDate, getModalityLabel } from '../lib/utils';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet';

const CITIES = ['Porto Velho', 'Ji-Paraná', 'Ariquemes', 'Vilhena', 'Cacoal', 'Rolim de Moura', 'Jaru', 'Guajará-Mirim', 'Ouro Preto do Oeste', 'Outras'];
const AREAS = ['TI', 'Comércio', 'Saúde', 'Indústria', 'Serviços', 'Educação', 'Agronegócio', 'Construção', 'Administrativo'];
const MODALITIES = [{ value: 'presencial', label: 'Presencial' }, { value: 'hibrido', label: 'Híbrido' }, { value: 'remoto', label: 'Remoto' }];

const JOB_CATEGORIES = [
  { id: 'vaga', label: 'Vaga de Emprego', color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20' },
  { id: 'concurso', label: 'Concurso Público', color: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20' },
  { id: 'estagio', label: 'Estágio / Trainee', color: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20' },
  { id: 'noticia', label: 'Notícia / Informativo', color: 'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-400 dark:border-cyan-500/20' },
  { id: 'eleicao', label: 'Eleição / Edital', color: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-500/10 dark:text-yellow-400 dark:border-yellow-500/20' },
  { id: 'curso', label: 'Curso / Treinamento', color: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20' },
  { id: 'freelance', label: 'Freelance', color: 'bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-500/10 dark:text-pink-400 dark:border-pink-500/20' },
  { id: 'voluntario', label: 'Voluntariado', color: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20' }
];

const getCategoryInfo = (catId) => {
    const cat = JOB_CATEGORIES.find(c => c.id === catId);
    return cat || { label: 'Oportunidade', color: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700' };
};

const JobCard = ({ job }) => {
  const categoryInfo = getCategoryInfo(job.category);
  if (!job.id && !job.slug) return null;

  // ✅ Prioriza o Slug para gerar a URL amigável
  const jobIdentifier = job.slug || job.id;

  return (
    <div className="group bg-white dark:bg-slate-900/60 backdrop-blur-sm rounded-2xl border border-slate-100 dark:border-slate-800 hover:border-teal-500/30 dark:hover:border-teal-500/50 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col md:flex-row">
      {job.image_url ? (
        <div className="w-full md:w-56 h-48 md:h-auto bg-slate-100 dark:bg-slate-800 relative flex-shrink-0 overflow-hidden">
            <img 
              src={job.image_url} 
              alt={job.title} 
              className={`w-full h-full group-hover:scale-105 transition-transform duration-700 opacity-90 group-hover:opacity-100 ${job.image_display_mode === 'contain' ? 'object-contain p-2' : 'object-cover'}`} 
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent dark:from-black/40" />
            <div className="absolute bottom-2 left-2 flex gap-1.5">
                {job.video_url && (
                    <div className="bg-red-600 text-white p-1.5 rounded shadow-lg animate-pulse" title="Possui vídeo">
                        <Play size={12} fill="currentColor" />
                    </div>
                )}
                {job.pdf_url && (
                    <div className="bg-teal-600 text-white p-1.5 rounded shadow-lg" title="Possui Edital/PDF">
                        <FileText size={12} />
                    </div>
                )}
            </div>
        </div>
      ) : (
        <div className={`hidden md:block w-1.5 ${categoryInfo.color.split(' ')[0]} h-auto`} />
      )}

      <div className="p-6 flex-1 flex flex-col justify-between relative">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-md text-[10px] md:text-xs font-bold uppercase tracking-wide border ${categoryInfo.color}`}>
                    {categoryInfo.label}
                </span>
                <span className="px-3 py-1 rounded-md text-[10px] md:text-xs font-medium border bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800/50 dark:text-slate-300 dark:border-slate-700">
                    {getModalityLabel(job.modality)}
                </span>
            </div>
            <div className="flex items-center text-xs text-slate-400 dark:text-slate-500 font-medium">
                <Clock className="w-3.5 h-3.5 mr-1.5" /> {formatDate(job.created_at)}
            </div>
        </div>

        <div className="mb-6">
            <Link to={`/vagas/${jobIdentifier}`}>
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors line-clamp-2 leading-tight">
                    {job.title}
                </h3>
            </Link>
            <div className="flex flex-wrap items-center gap-y-2 gap-x-5 text-sm text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-teal-500 dark:text-teal-400" /> 
                    <span className="font-semibold text-slate-700 dark:text-slate-300">{job.company_name}</span>
                </span>
                <span className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-teal-500 dark:text-teal-400" /> {job.city}
                </span>
            </div>
        </div>

        <div className="pt-5 mt-auto border-t border-slate-100 dark:border-slate-800/50 flex items-center justify-between">
            <div className="flex flex-col">
                <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-slate-500 tracking-wider">Salário / Bolsa</span>
                <span className="text-lg font-bold text-teal-700 dark:text-teal-400">
                    {formatSalary(job.salary_min, job.salary_max)}
                </span>
            </div>

            <Link to={`/vagas/${jobIdentifier}`}>
                <button className="flex items-center justify-center rounded-xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 hover:bg-teal-600 dark:hover:bg-teal-400 dark:hover:text-white transition-all shadow-md px-6 py-2.5 text-sm font-bold group/btn">
                    Ver Detalhes <ArrowUpRight className="w-4 h-4 ml-2 group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5 transition-transform" />
                </button>
            </Link>
        </div>
      </div>
    </div>
  );
};

const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ search: '', city: '', area: '', modality: '', category: '' });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const loadJobs = async () => {
        try {
          setLoading(true);
          const params = {};
          if (filters.search) params.search = filters.search;
          if (filters.city && filters.city !== 'all') params.city = filters.city;
          if (filters.area && filters.area !== 'all') params.area = filters.area;
          if (filters.modality && filters.modality !== 'all') params.modality = filters.modality;
          if (filters.category && filters.category !== 'all') params.category = filters.category;
          
          const res = await jobsAPI.getAll(params);
          setJobs(res.data);
        } catch (err) {
          console.error("Erro:", err);
          toast.error('Erro ao carregar oportunidades.');
        } finally {
          setLoading(false);
        }
      };
      loadJobs();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [filters]); 

  const handleSearch = (e) => { e.preventDefault(); };
  const clearFilters = () => setFilters({ search: '', city: '', area: '', modality: '', category: '' });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617] transition-colors duration-300 font-sans pb-20">
      <Helmet>
        <title>Emprega Mais RO | Vagas e Oportunidades em Rondônia</title>
        <meta name="description" content="Encontre as melhores vagas de emprego, concursos e notícias em Rondônia." />
      </Helmet>
      
      {/* HERO SECTION */}
      <div className="bg-gradient-to-br from-teal-600 to-emerald-600 dark:from-slate-950 dark:via-[#0f172a] dark:to-slate-900 py-16 px-4 mb-8 border-b border-teal-500/20 dark:border-slate-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-teal-500/10 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="container mx-auto max-w-6xl relative z-10">
            <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="text-white space-y-4">
                    <div className="inline-flex items-center gap-2 bg-white/10 dark:bg-teal-500/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border border-white/20 dark:border-teal-500/30">
                        <Sparkles className="w-3 h-3 text-yellow-300" /> Oportunidades em Rondônia
                    </div>
                    <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight">
                        Sua próxima etapa <br/>começa aqui.
                    </h1>
                </div>
                {/* Botão de contato/anúncio */}
                <div className="bg-white/10 dark:bg-slate-900/40 backdrop-blur-md border border-white/20 dark:border-slate-700/50 p-6 rounded-2xl text-white relative">
                    <div className="flex items-start gap-4">
                        <div className="bg-white dark:bg-teal-500 text-teal-600 dark:text-slate-900 p-3 rounded-xl shadow-lg">
                            <Megaphone className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="font-bold text-xl mb-1">Anuncie gratuitamente</h3>
                            <Link to="/contato">
                                <span className="inline-flex items-center text-sm font-bold bg-white dark:bg-teal-500 text-teal-700 dark:text-white px-4 py-2 rounded-lg hover:bg-teal-50 transition-colors mt-2">
                                    Começar agora <ArrowUpRight className="w-3 h-3 ml-2" />
                                </span>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-6xl -mt-12 relative z-20">
        {/* FILTROS */}
        <div className="bg-white dark:bg-slate-900/90 backdrop-blur-md p-4 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 mb-10">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input 
                        className="w-full pl-12 h-14 border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 rounded-xl outline-none focus:ring-2 focus:ring-teal-500/20"
                        placeholder="Busque por cargo, empresa ou palavra-chave..." 
                        value={filters.search} 
                        onChange={(e) => setFilters({...filters, search: e.target.value})} 
                    />
                </div>
                <Button 
                    type="button" 
                    onClick={() => setShowFilters(!showFilters)} 
                    variant="outline"
                    className="h-14 px-6 rounded-xl"
                >
                    <Filter className="w-5 h-5 mr-2" /> Filtros
                </Button>
            </form>

            {showFilters && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 animate-in fade-in zoom-in duration-200">
                    <Select value={filters.category} onValueChange={(v) => setFilters({...filters, category: v})}>
                        <SelectTrigger className="h-12"><SelectValue placeholder="Tipo" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todas Categorias</SelectItem>
                            {JOB_CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={filters.city} onValueChange={(v) => setFilters({...filters, city: v})}>
                        <SelectTrigger className="h-12"><SelectValue placeholder="Cidade" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">Todas as Cidades</SelectItem>{CITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={filters.area} onValueChange={(v) => setFilters({...filters, area: v})}>
                        <SelectTrigger className="h-12"><SelectValue placeholder="Área" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">Todas as Áreas</SelectItem>{AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={filters.modality} onValueChange={(v) => setFilters({...filters, modality: v})}>
                        <SelectTrigger className="h-12"><SelectValue placeholder="Modalidade" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todas</SelectItem>
                            {MODALITIES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            )}
        </div>

        {/* LISTAGEM */}
        <div className="space-y-6">
          {loading ? (
             <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-48 bg-white dark:bg-slate-900 rounded-2xl animate-pulse" />
                ))}
             </div>
          ) : jobs.length > 0 ? (
            jobs.map(job => <JobCard key={job.id} job={job} />)
          ) : (
            <div className="text-center py-24 bg-white dark:bg-slate-900/60 rounded-3xl border border-dashed border-slate-300">
                <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-700 dark:text-white">Nenhuma vaga encontrada</h3>
                <Button variant="link" onClick={clearFilters} className="text-teal-600">Limpar Filtros</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobsPage;